import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Truck, LogOut, MoreHorizontal, Trash2, Phone, Mail, ImageIcon, DollarSign, CheckCircle2, Search, Link2, Copy, ExternalLink, MapPin, CalendarDays, Send, X as XIcon } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { ADMIN } from "@/constants/testIds";
import { toast } from "sonner";

const statusColors = {
  new: "bg-[#5BB239] text-white",
  contacted: "bg-amber-500 text-white",
  scheduled: "bg-[#223932] text-white",
  completed: "bg-emerald-700 text-white",
  cancelled: "bg-zinc-400 text-white",
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [callbacks, setCallbacks] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [chatLeads, setChatLeads] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [openBookingId, setOpenBookingId] = useState(null);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, b, c, r, cl] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/bookings"),
        api.get("/admin/callbacks"),
        api.get("/admin/referrals"),
        api.get("/admin/chat/leads"),
      ]);
      setStats(s.data);
      setBookings(b.data);
      setCallbacks(c.data);
      setReferrals(r.data);
      setChatLeads(cl.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}`, { status });
      setBookings((cur) => cur.map((b) => (b.id === id ? { ...b, status } : b)));
      toast.success(`Marked ${status}`);
      api.get("/admin/stats").then((r) => setStats(r.data));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const updateDeposit = async (id, deposit_status) => {
    try {
      const { data } = await api.patch(`/admin/bookings/${id}/deposit`, { status: deposit_status });
      const paid_at = deposit_status === "paid" ? new Date().toISOString() : null;
      setBookings((cur) =>
        cur.map((b) =>
          b.id === id ? { ...b, deposit_status: data.deposit_status, deposit_paid_at: paid_at } : b
        )
      );
      toast.success(
        deposit_status === "paid"
          ? "Deposit marked PAID · customer notified"
          : "Deposit reset to pending"
      );
      api.get("/admin/stats").then((r) => setStats(r.data));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const updatePaymentLink = async (id, payment_link) => {
    try {
      const { data } = await api.patch(`/admin/bookings/${id}/payment-link`, { payment_link });
      setBookings((cur) =>
        cur.map((b) => (b.id === id ? { ...b, payment_link: data.payment_link } : b))
      );
      toast.success(
        payment_link
          ? "Payment link saved · customer emailed"
          : "Payment link cleared"
      );
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to save link");
    }
  };

  const updateReferralCredit = async (id, status) => {
    try {
      await api.patch(`/admin/referrals/${id}/credit`, { status });
      const credited_at = status === "issued" ? new Date().toISOString() : null;
      setReferrals((cur) =>
        cur.map((r) => (r.id === id ? { ...r, credit_status: status, credited_at } : r))
      );
      toast.success(status === "issued" ? "Credit marked as issued" : "Credit reset to pending");
      api.get("/admin/stats").then((res) => setStats(res.data));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this booking?")) return;
    try {
      await api.delete(`/admin/bookings/${id}`);
      setBookings((cur) => cur.filter((b) => b.id !== id));
      toast.success("Deleted");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } finally {
      // Hard navigate to fully clear React state and avoid stale ProtectedRoute decisions
      window.location.href = "/admin";
    }
  };

  const filtered = bookings.filter((b) => {
    if (filter !== "all" && b.status !== filter) return false;
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (
      (b.full_name || "").toLowerCase().includes(s) ||
      (b.email || "").toLowerCase().includes(s) ||
      (b.phone || "").toLowerCase().includes(s) ||
      (b.id || "").toLowerCase().includes(s) ||
      (b.origin_city || "").toLowerCase().includes(s) ||
      (b.destination_city || "").toLowerCase().includes(s)
    );
  });

  // Revenue metrics
  const revenueLow = bookings.reduce((sum, b) => sum + (b.estimated_quote?.low || 0), 0);
  const revenueHigh = bookings.reduce((sum, b) => sum + (b.estimated_quote?.high || 0), 0);
  const depositsCollected = bookings.filter((b) => b.deposit_status === "paid").length * 100;
  const openBooking = bookings.find((b) => b.id === openBookingId) || null;

  return (
    <div className="min-h-screen bg-white" data-testid="admin-dashboard-page">
      <header className="bg-[#151816] text-white sticky top-0 z-30">
        <div className="px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#5BB239] flex items-center justify-center">
              <Truck className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-display font-extrabold text-sm tracking-tight">
                ThemBoys<span className="text-[#5BB239]">.</span> Console
              </div>
              <div className="font-mono-label text-[9px] text-white/50 mt-0.5">
                Dispatch & Operations
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="font-mono-label text-[10px] text-white/60 hover:text-white">
              View Site
            </Link>
            <span className="hidden sm:inline text-xs text-white/60">{user?.email}</span>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 bg-[#5BB239] px-4 py-2 font-mono-label text-[10px] hover:bg-[#499828] transition-colors"
              data-testid={ADMIN.logoutBtn}
            >
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="px-6 py-10 max-w-7xl mx-auto">
        <div className="overline">Overview</div>
        <h1 className="mt-2 font-display font-black text-4xl tracking-tight">
          Operations Dashboard
        </h1>

        {/* Revenue Strip */}
        <div className="mt-6 bg-[#151816] text-white p-6 lg:p-8 grid sm:grid-cols-3 gap-6" data-testid="revenue-strip">
          <div>
            <div className="font-mono-label text-[10px] text-[#5BB239] tracking-widest">Pipeline Value</div>
            <div className="mt-2 font-display font-black text-3xl lg:text-4xl tracking-tight">
              ${revenueLow.toLocaleString()}–${revenueHigh.toLocaleString()}
            </div>
            <div className="mt-1 text-[11px] text-white/50">Sum of all booked estimates</div>
          </div>
          <div className="sm:border-l sm:border-white/10 sm:pl-6">
            <div className="font-mono-label text-[10px] text-emerald-400 tracking-widest">Deposits Collected</div>
            <div className="mt-2 font-display font-black text-3xl lg:text-4xl tracking-tight">
              ${depositsCollected.toLocaleString()}
            </div>
            <div className="mt-1 text-[11px] text-white/50">
              {bookings.filter((b) => b.deposit_status === "paid").length} paid ·
              {" "}{bookings.filter((b) => b.deposit_status === "pending").length} pending
            </div>
          </div>
          <div className="sm:border-l sm:border-white/10 sm:pl-6">
            <div className="font-mono-label text-[10px] text-amber-400 tracking-widest">Needs Action</div>
            <div className="mt-2 font-display font-black text-3xl lg:text-4xl tracking-tight">
              {(stats?.new_bookings ?? 0) + (stats?.callbacks ?? 0)}
            </div>
            <div className="mt-1 text-[11px] text-white/50">
              New bookings + open callbacks
            </div>
          </div>
        </div>

        <div
          className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-px bg-[#DDD8C9] border border-[#DDD8C9]"
          data-testid={ADMIN.statRow}
        >
          <Stat label="Total" value={stats?.total_bookings ?? 0} />
          <Stat label="New" value={stats?.new_bookings ?? 0} accent />
          <Stat label="Deposit Due" value={stats?.deposits_pending ?? 0} warn />
          <Stat label="Scheduled" value={stats?.scheduled ?? 0} />
          <Stat label="Completed" value={stats?.completed ?? 0} />
          <Stat label="Callbacks" value={stats?.callbacks ?? 0} />
          <Stat label="Quotes Run" value={stats?.quotes ?? 0} />
        </div>

        <Tabs defaultValue="bookings" className="mt-12">
          <TabsList className="bg-transparent border-b border-[#DDD8C9] w-full justify-start rounded-none h-auto p-0">
            <TabsTrigger value="bookings" className="rounded-none data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[#5BB239] data-[state=active]:shadow-none px-5 py-3 font-mono-label text-[11px]">
              Bookings ({bookings.length})
            </TabsTrigger>
            <TabsTrigger value="callbacks" className="rounded-none data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[#5BB239] data-[state=active]:shadow-none px-5 py-3 font-mono-label text-[11px]">
              Callbacks ({callbacks.length})
            </TabsTrigger>
            <TabsTrigger value="referrals" className="rounded-none data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[#5BB239] data-[state=active]:shadow-none px-5 py-3 font-mono-label text-[11px]" data-testid="tab-referrals">
              Referrals ({referrals.length})
              {(stats?.referrals_pending_credit ?? 0) > 0 && (
                <span className="ml-1.5 bg-amber-500 text-white text-[9px] px-1.5 py-0.5">
                  {stats.referrals_pending_credit}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="chat-leads" className="rounded-none data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[#5BB239] data-[state=active]:shadow-none px-5 py-3 font-mono-label text-[11px]" data-testid="tab-chat-leads">
              Chat Leads ({chatLeads.length})
              {(stats?.chat_leads_new ?? 0) > 0 && (
                <span className="ml-1.5 bg-[#5BB239] text-black text-[9px] px-1.5 py-0.5">
                  {stats.chat_leads_new} NEW
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="bookings" className="mt-8">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
              <div className="font-display font-bold text-2xl">Customer Bookings</div>
              <div className="flex items-center gap-2 flex-1 sm:flex-none sm:max-w-lg w-full">
                <div className="relative flex-1">
                  <Search className="w-4 h-4 text-[#5C635F] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <Input
                    placeholder="Search by name, email, phone, city, or ID..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9 rounded-none border-[#DDD8C9] h-10"
                    data-testid="admin-search-input"
                  />
                  {search && (
                    <button
                      onClick={() => setSearch("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-[#F5F3EC]"
                      data-testid="admin-search-clear"
                    >
                      <XIcon className="w-3.5 h-3.5 text-[#5C635F]" />
                    </button>
                  )}
                </div>
                <Select value={filter} onValueChange={setFilter}>
                  <SelectTrigger className="w-40 rounded-none border-[#DDD8C9]" data-testid="status-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="contacted">Contacted</SelectItem>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {filtered.length !== bookings.length && (
              <div className="mb-3 text-[11px] font-mono-label text-[#5C635F]" data-testid="filter-count">
                Showing {filtered.length} of {bookings.length} bookings
              </div>
            )}

            <div className="border border-[#DDD8C9]" data-testid={ADMIN.bookingsTable}>
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F5F3EC] hover:bg-[#F5F3EC]">
                    <TableHead className="font-mono-label text-[10px]">Customer</TableHead>
                    <TableHead className="font-mono-label text-[10px]">Move</TableHead>
                    <TableHead className="font-mono-label text-[10px]">From → To</TableHead>
                    <TableHead className="font-mono-label text-[10px]">Date</TableHead>
                    <TableHead className="font-mono-label text-[10px]">Est.</TableHead>
                    <TableHead className="font-mono-label text-[10px]">Deposit</TableHead>
                    <TableHead className="font-mono-label text-[10px]">Photos</TableHead>
                    <TableHead className="font-mono-label text-[10px]">Status</TableHead>
                    <TableHead className="font-mono-label text-[10px] w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={9} className="text-center py-12 text-[#5C635F]">Loading…</TableCell></TableRow>
                  ) : filtered.length === 0 ? (
                    <TableRow><TableCell colSpan={9} className="text-center py-12 text-[#5C635F]">No bookings yet</TableCell></TableRow>
                  ) : filtered.map((b) => (
                    <TableRow key={b.id} className="border-t border-[#DDD8C9]" data-testid={`booking-row-${b.id}`}>
                      <TableCell>
                        <button
                          className="font-bold text-left hover:text-[#5BB239] transition-colors"
                          onClick={() => setOpenBookingId(b.id)}
                          data-testid={`open-details-${b.id}`}
                        >
                          {b.full_name}
                        </button>
                        <div className="flex gap-2 text-[11px] text-[#5C635F] mt-0.5">
                          <Phone className="w-3 h-3" /> {b.phone}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-xs">{b.move_type.replace("_", " ")}</div>
                        <div className="text-[10px] text-[#5C635F] uppercase">{b.home_size}</div>
                      </TableCell>
                      <TableCell className="text-xs">
                        {b.origin_city}, {b.origin_state}
                        <div className="text-[#5BB239]">→ {b.destination_city}, {b.destination_state}</div>
                      </TableCell>
                      <TableCell className="text-xs font-mono-label">{b.move_date}</TableCell>
                      <TableCell className="text-xs">
                        ${b.estimated_quote?.low?.toLocaleString()}–${b.estimated_quote?.high?.toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <DepositCell booking={b} onUpdate={updateDeposit} />
                      </TableCell>
                      <TableCell>
                        <PhotosCell booking={b} />
                      </TableCell>
                      <TableCell>
                        <Select value={b.status} onValueChange={(v) => updateStatus(b.id, v)}>
                          <SelectTrigger className={`rounded-none border-0 h-8 px-2 text-[10px] font-mono-label uppercase ${statusColors[b.status]}`} data-testid={`status-${b.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="new">New</SelectItem>
                            <SelectItem value="contacted">Contacted</SelectItem>
                            <SelectItem value="scheduled">Scheduled</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                            <SelectItem value="cancelled">Cancelled</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="p-1 hover:bg-[#F5F3EC]" data-testid={`row-menu-${b.id}`}>
                              <MoreHorizontal className="w-4 h-4" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setOpenBookingId(b.id)}>
                              <ExternalLink className="w-3.5 h-3.5 mr-2" /> Open Details
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => window.open("https://app.qbo.intuit.com/app/invoice", "_blank")}>
                              <DollarSign className="w-3.5 h-3.5 mr-2" /> Open QuickBooks
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => window.location.href = `mailto:${b.email}?subject=ThemBoysMoving%20%23${b.id.slice(0,8).toUpperCase()}%20-%20%24100%20Deposit%20Invoice&body=Hi%20${encodeURIComponent(b.full_name)}%2C%0A%0AAttached%20is%20your%20%24100%20QuickBooks%20deposit%20invoice%20to%20lock%20in%20your%20moving%20date%20on%20${b.move_date}.%20Once%20paid%2C%20your%20date%20is%20officially%20locked%20and%20the%20deposit%20applies%20to%20your%20final%20bill.%0A%0AThanks%2C%0AThemBoysMoving%20%26%20Storage`}>
                              <Mail className="w-3.5 h-3.5 mr-2" /> Email customer
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => remove(b.id)} className="text-red-600">
                              <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="callbacks" className="mt-8">
            <div className="font-display font-bold text-2xl mb-5">Callback Requests</div>
            <div className="border border-[#DDD8C9]">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F5F3EC] hover:bg-[#F5F3EC]">
                    <TableHead className="font-mono-label text-[10px]">Name</TableHead>
                    <TableHead className="font-mono-label text-[10px]">Phone</TableHead>
                    <TableHead className="font-mono-label text-[10px]">Email</TableHead>
                    <TableHead className="font-mono-label text-[10px]">Best Time</TableHead>
                    <TableHead className="font-mono-label text-[10px]">Message</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {callbacks.length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="text-center py-12 text-[#5C635F]">No callback requests</TableCell></TableRow>
                  ) : callbacks.map((c) => (
                    <TableRow key={c.id} className="border-t border-[#DDD8C9]">
                      <TableCell className="font-bold">{c.full_name}</TableCell>
                      <TableCell>{c.phone}</TableCell>
                      <TableCell className="text-xs">{c.email || "—"}</TableCell>
                      <TableCell className="text-xs">{c.best_time || "—"}</TableCell>
                      <TableCell className="text-xs max-w-md text-[#5C635F]">{c.message || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="referrals" className="mt-8">
            <div className="flex items-center justify-between mb-5">
              <div>
                <div className="font-display font-bold text-2xl">Referral Program</div>
                <div className="text-xs text-[#5C635F] mt-1">
                  Give $25, get $25 · {referrals.length} total · {referrals.filter(r => r.credit_status === "pending").length} pending credit
                </div>
              </div>
            </div>
            <div className="border border-[#DDD8C9]" data-testid="admin-referrals-table">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F5F3EC] hover:bg-[#F5F3EC]">
                    <TableHead className="font-mono-label text-[10px]">Code</TableHead>
                    <TableHead className="font-mono-label text-[10px]">Referrer</TableHead>
                    <TableHead className="font-mono-label text-[10px]">Referred</TableHead>
                    <TableHead className="font-mono-label text-[10px]">Amount</TableHead>
                    <TableHead className="font-mono-label text-[10px]">Date</TableHead>
                    <TableHead className="font-mono-label text-[10px]">Credit</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {referrals.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-12 text-[#5C635F]">
                      No referrals yet. Customers get a shareable code on their booking success page.
                    </TableCell></TableRow>
                  ) : referrals.map((r) => (
                    <TableRow key={r.id} className="border-t border-[#DDD8C9]" data-testid={`referral-row-${r.id}`}>
                      <TableCell>
                        <span className="font-mono-label text-[11px] tracking-widest bg-[#5BB239]/10 text-[#5BB239] px-2 py-1">
                          {r.code}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="font-bold text-sm">{r.referrer_name}</div>
                        <div className="text-[11px] text-[#5C635F]">{r.referrer_email}</div>
                      </TableCell>
                      <TableCell>
                        <div className="font-bold text-sm">{r.referee_name}</div>
                        <div className="text-[11px] text-[#5C635F]">{r.referee_email}</div>
                      </TableCell>
                      <TableCell className="font-display font-black text-[#5BB239]">
                        ${r.credit_amount}
                      </TableCell>
                      <TableCell className="text-xs">
                        {new Date(r.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        {r.credit_status === "issued" ? (
                          <div className="inline-flex items-center gap-1.5 bg-emerald-100 text-emerald-800 px-2 py-1" data-testid={`referral-credit-cell-${r.id}`}>
                            <CheckCircle2 className="w-3 h-3" />
                            <span className="text-[10px] font-mono-label uppercase tracking-widest">Issued</span>
                          </div>
                        ) : (
                          <button
                            onClick={() => updateReferralCredit(r.id, "issued")}
                            className="inline-flex items-center gap-1.5 bg-amber-500 hover:bg-amber-600 text-white px-2.5 py-1 transition-colors"
                            data-testid={`referral-mark-issued-${r.id}`}
                          >
                            <DollarSign className="w-3 h-3" />
                            <span className="text-[10px] font-mono-label uppercase tracking-widest">Mark Issued</span>
                          </button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="chat-leads" className="mt-8">
            <ChatLeadsTable
              leads={chatLeads}
              onStatusChange={async (id, status) => {
                try {
                  await api.patch(`/admin/chat/leads/${id}`, { status });
                  setChatLeads((cur) =>
                    cur.map((l) => (l.id === id ? { ...l, status } : l))
                  );
                  toast.success(`Lead marked ${status}`);
                  api.get("/admin/stats").then((r) => setStats(r.data));
                } catch (e) {
                  toast.error(formatApiError(e.response?.data?.detail));
                }
              }}
            />
          </TabsContent>
        </Tabs>
      </main>

      {/* Booking Details Sheet */}
      <Sheet open={!!openBookingId} onOpenChange={(o) => !o && setOpenBookingId(null)}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-xl bg-[#F5F3EC] border-l-4 border-[#5BB239] rounded-none overflow-y-auto"
          data-testid="booking-details-sheet"
        >
          {openBooking && (
            <BookingDetails
              booking={openBooking}
              onUpdateStatus={updateStatus}
              onUpdateDeposit={updateDeposit}
              onUpdatePaymentLink={updatePaymentLink}
              onClose={() => setOpenBookingId(null)}
            />
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function Stat({ label, value, accent, warn }) {
  const bg = accent
    ? "bg-[#5BB239] text-white"
    : warn && value > 0
      ? "bg-amber-500 text-white"
      : "bg-white";
  const labelColor = accent || (warn && value > 0) ? "text-white/70" : "text-[#5C635F]";
  return (
    <div className={`p-5 ${bg}`}>
      <div className={`font-mono-label text-[10px] ${labelColor}`}>{label}</div>
      <div className="mt-1 font-display font-black text-3xl tracking-tight">{value}</div>
    </div>
  );
}

function BookingDetails({ booking: b, onUpdateStatus, onUpdateDeposit, onUpdatePaymentLink, onClose }) {
  const [paymentLink, setPaymentLink] = useState(b.payment_link || "");
  const [savingLink, setSavingLink] = useState(false);
  const bid = b.id.slice(0, 8).toUpperCase();

  const saveLink = async () => {
    setSavingLink(true);
    await onUpdatePaymentLink(b.id, paymentLink.trim());
    setSavingLink(false);
  };

  const copy = (text, label) => {
    navigator.clipboard.writeText(text).then(() => toast.success(`Copied ${label}`));
  };

  const trackUrl = `${window.location.origin}/track`;
  const mailtoLink = paymentLink
    ? `mailto:${b.email}?subject=${encodeURIComponent(`Pay Your $100 Deposit — ThemBoysMoving #${bid}`)}&body=${encodeURIComponent(
        `Hi ${b.full_name},\n\nYour $100 QuickBooks deposit invoice is ready. Once paid, your ${b.move_date} moving date is officially locked.\n\nPay here: ${paymentLink}\n\nOr track your booking: ${trackUrl}\n\nThanks,\nThemBoysMoving & Storage\n(502) 507-7904`
      )}`
    : null;

  return (
    <div className="pb-8">
      <SheetHeader className="pb-4 border-b border-[#DDD8C9]">
        <SheetTitle className="font-display font-black text-2xl tracking-tight text-[#151816]">
          {b.full_name}
        </SheetTitle>
        <div className="flex items-center gap-2 text-[11px] font-mono-label text-[#5C635F]">
          <span className="bg-[#151816] text-white px-2 py-0.5 tracking-widest">#{bid}</span>
          <span>·</span>
          <span>Created {new Date(b.created_at).toLocaleDateString()}</span>
        </div>
      </SheetHeader>

      {/* Quick actions */}
      <div className="grid grid-cols-2 gap-2 mt-5">
        <a
          href={`tel:${b.phone}`}
          className="flex items-center justify-center gap-2 bg-[#5BB239] text-white px-3 py-3 font-bold text-xs uppercase tracking-widest hover:bg-[#499828]"
          data-testid="details-call-btn"
        >
          <Phone className="w-3.5 h-3.5" /> Call
        </a>
        <a
          href={`mailto:${b.email}`}
          className="flex items-center justify-center gap-2 bg-[#151816] text-white px-3 py-3 font-bold text-xs uppercase tracking-widest hover:bg-[#2a2f2c]"
          data-testid="details-email-btn"
        >
          <Mail className="w-3.5 h-3.5" /> Email
        </a>
      </div>

      {/* Deposit block */}
      <div className="mt-6 bg-white border border-[#DDD8C9] p-5">
        <div className="font-mono-label text-[10px] text-[#5BB239] tracking-widest">Deposit · $100</div>
        {b.deposit_status === "paid" ? (
          <div className="mt-3 flex items-center gap-2 bg-emerald-50 text-emerald-800 px-3 py-2">
            <CheckCircle2 className="w-4 h-4" />
            <div>
              <div className="text-xs font-bold uppercase tracking-widest">Paid</div>
              {b.deposit_paid_at && (
                <div className="text-[10px] text-emerald-700/70 mt-0.5">
                  {new Date(b.deposit_paid_at).toLocaleString()}
                </div>
              )}
            </div>
            <button
              onClick={() => onUpdateDeposit(b.id, "pending")}
              className="ml-auto text-[10px] font-mono-label uppercase text-[#5C635F] hover:text-[#151816]"
              data-testid="details-unmark-deposit"
            >
              Undo
            </button>
          </div>
        ) : (
          <div className="mt-3">
            <button
              onClick={() => onUpdateDeposit(b.id, "paid")}
              className="w-full flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 text-white px-4 py-3 font-bold text-xs uppercase tracking-widest transition-colors"
              data-testid="details-mark-deposit-paid"
            >
              <DollarSign className="w-4 h-4" /> Mark $100 Deposit Paid
            </button>
            <div className="mt-2 text-[10px] text-[#5C635F]">
              Click once the QuickBooks invoice shows as PAID. Customer gets an email + SMS confirmation.
            </div>
          </div>
        )}
      </div>

      {/* Payment Link (QuickBooks) */}
      <div className="mt-4 bg-white border border-[#DDD8C9] p-5">
        <div className="font-mono-label text-[10px] text-[#5BB239] tracking-widest flex items-center gap-2">
          <Link2 className="w-3 h-3" /> QuickBooks Pay Now Link
        </div>
        <p className="text-[11px] text-[#5C635F] mt-1.5 leading-relaxed">
          Paste the "Share Link" from your QuickBooks invoice. Saving it emails/texts the customer a
          "Pay $100 Deposit" button on their tracking page.
        </p>
        <div className="mt-3 flex gap-2">
          <Input
            value={paymentLink}
            onChange={(e) => setPaymentLink(e.target.value)}
            placeholder="https://quickbooks.intuit.com/pay/..."
            className="rounded-none border-[#DDD8C9] h-10 text-xs"
            data-testid="details-payment-link-input"
          />
          <button
            onClick={saveLink}
            disabled={savingLink || paymentLink.trim() === (b.payment_link || "")}
            className="flex items-center gap-2 bg-[#5BB239] hover:bg-[#499828] disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 font-mono-label text-[10px] uppercase tracking-widest"
            data-testid="details-payment-link-save"
          >
            <Send className="w-3.5 h-3.5" />
            {savingLink ? "Saving..." : "Save & Send"}
          </button>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            onClick={() => window.open("https://app.qbo.intuit.com/app/invoice", "_blank")}
            className="text-[10px] font-mono-label uppercase text-[#5BB239] hover:underline flex items-center gap-1"
            data-testid="details-open-quickbooks"
          >
            <ExternalLink className="w-3 h-3" /> Open QuickBooks
          </button>
          {b.payment_link && (
            <>
              <span className="text-[10px] text-[#5C635F]">·</span>
              <button
                onClick={() => copy(b.payment_link, "payment link")}
                className="text-[10px] font-mono-label uppercase text-[#5C635F] hover:text-[#151816] flex items-center gap-1"
                data-testid="details-copy-payment-link"
              >
                <Copy className="w-3 h-3" /> Copy link
              </button>
              <span className="text-[10px] text-[#5C635F]">·</span>
              {mailtoLink && (
                <a
                  href={mailtoLink}
                  className="text-[10px] font-mono-label uppercase text-[#5C635F] hover:text-[#151816] flex items-center gap-1"
                  data-testid="details-mailto-payment-link"
                >
                  <Mail className="w-3 h-3" /> Email link
                </a>
              )}
            </>
          )}
        </div>
      </div>

      {/* Contact / Move info */}
      <div className="mt-4 bg-white border border-[#DDD8C9] p-5 space-y-3">
        <InfoRow label="Email" value={b.email} onCopy={() => copy(b.email, "email")} />
        <InfoRow label="Phone" value={b.phone} onCopy={() => copy(b.phone, "phone")} />
        <InfoRow label="SMS Opt-in" value={b.sms_opt_in ? "Yes" : "No"} />
        <InfoRow
          label="Move"
          value={`${(b.move_type || "").replace("_", " ")} · ${(b.home_size || "").toUpperCase()}`}
        />
        <InfoRow
          label="Route"
          value={`${b.origin_city}, ${b.origin_state} → ${b.destination_city}, ${b.destination_state}`}
        />
        <InfoRow label="Move Date" value={b.move_date} icon={CalendarDays} />
        <InfoRow
          label="Add-ons"
          value={(b.services || []).join(", ") || "None"}
        />
        {b.estimated_quote && (
          <InfoRow
            label="Estimate"
            value={`$${b.estimated_quote.low.toLocaleString()} – $${b.estimated_quote.high.toLocaleString()}`}
          />
        )}
        {b.notes && <InfoRow label="Notes" value={b.notes} multiline />}
      </div>

      {/* Status changer */}
      <div className="mt-4 bg-white border border-[#DDD8C9] p-5">
        <div className="font-mono-label text-[10px] text-[#5BB239] tracking-widest">Booking Status</div>
        <div className="mt-2 grid grid-cols-5 gap-2">
          {["new", "contacted", "scheduled", "completed", "cancelled"].map((s) => (
            <button
              key={s}
              onClick={() => onUpdateStatus(b.id, s)}
              className={`px-2 py-2 text-[9px] font-mono-label uppercase tracking-widest transition-colors ${
                b.status === s
                  ? "bg-[#151816] text-white"
                  : "bg-[#EBE8DD] text-[#5C635F] hover:bg-[#DDD8C9]"
              }`}
              data-testid={`details-status-${s}`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}


function InfoRow({ label, value, onCopy, icon: Icon, multiline }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-[90px]">
        <div className="font-mono-label text-[9px] text-[#5C635F] tracking-widest uppercase">{label}</div>
      </div>
      <div className="flex-1 flex items-start gap-2 min-w-0">
        {Icon && <Icon className="w-3.5 h-3.5 text-[#5C635F] mt-0.5 flex-shrink-0" />}
        <div className={`text-sm text-[#151816] break-words ${multiline ? "whitespace-pre-wrap" : ""}`}>
          {value}
        </div>
      </div>
      {onCopy && (
        <button
          onClick={onCopy}
          className="p-1 hover:bg-[#EBE8DD] flex-shrink-0"
          title="Copy"
        >
          <Copy className="w-3 h-3 text-[#5C635F]" />
        </button>
      )}
    </div>
  );
}


function DepositCell({ booking, onUpdate }) {
  const status = booking.deposit_status || "pending";
  const amount = booking.deposit_amount || 100;
  if (status === "paid") {
    return (
      <div className="inline-flex items-center gap-1.5 bg-emerald-100 text-emerald-800 px-2 py-1" data-testid={`deposit-cell-${booking.id}`}>
        <CheckCircle2 className="w-3 h-3" />
        <span className="text-[10px] font-mono-label uppercase tracking-widest">${amount} Paid</span>
      </div>
    );
  }
  return (
    <button
      onClick={() => onUpdate(booking.id, "paid")}
      className="inline-flex items-center gap-1.5 bg-amber-500 hover:bg-amber-600 text-white px-2.5 py-1 transition-colors"
      data-testid={`mark-deposit-paid-${booking.id}`}
      title={`Send $${amount} QuickBooks invoice to ${booking.email}, then click here once paid`}
    >
      <DollarSign className="w-3 h-3" />
      <span className="text-[10px] font-mono-label uppercase tracking-widest">Mark ${amount} Paid</span>
    </button>
  );
}


function PhotosCell({ booking }) {
  const photos = booking.photos || [];
  if (photos.length === 0) {
    return <span className="text-xs text-[#5C635F]">—</span>;
  }
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          className="inline-flex items-center gap-1.5 bg-[#EBE8DD] hover:bg-[#DDD8C9] px-2.5 py-1 transition-colors"
          data-testid={`photos-btn-${booking.id}`}
        >
          <ImageIcon className="w-3 h-3 text-[#5BB239]" />
          <span className="text-xs font-mono-label">{photos.length}</span>
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[#F5F3EC] max-w-3xl rounded-none border border-[#DDD8C9]">
        <DialogHeader>
          <DialogTitle className="font-display font-bold text-2xl">
            {booking.full_name}'s inventory
            <span className="ml-3 font-mono-label text-[10px] text-[#5C635F]">
              {photos.length} photo{photos.length > 1 ? "s" : ""}
            </span>
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4 max-h-[60vh] overflow-y-auto">
          {photos.map((p) => (
            <AdminPhoto key={p.id} bookingId={booking.id} photo={p} />
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AdminPhoto({ bookingId, photo }) {
  const [src, setSrc] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let cancelled = false;
    let url = null;
    (async () => {
      try {
        const res = await api.get(`/admin/bookings/${bookingId}/photos/${photo.id}`, {
          responseType: "blob",
        });
        if (cancelled) return;
        url = URL.createObjectURL(res.data);
        setSrc(url);
      } catch (e) {
        console.error("Photo fetch failed:", e?.response?.status, e?.message);
        if (!cancelled) setErr(true);
      }
    })();
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [bookingId, photo.id]);

  return (
    <div className="block aspect-square bg-black/5 border border-[#DDD8C9] hover:border-[#5BB239] transition-colors overflow-hidden">
      {src ? (
        <a href={src} target="_blank" rel="noopener noreferrer">
          <img
            src={src}
            alt={photo.original_filename}
            className="w-full h-full object-cover"
            data-testid={`admin-photo-img-${photo.id}`}
          />
        </a>
      ) : (
        <div className="w-full h-full flex items-center justify-center text-xs text-[#5C635F]/60">
          {err ? "Failed" : <ImageIcon className="w-5 h-5 text-[#5C635F]/30" />}
        </div>
      )}
    </div>
  );
}

// ---------- Chat Leads Tab ----------
const leadStatusColors = {
  new: "bg-[#5BB239] text-black",
  contacted: "bg-amber-500 text-white",
  converted: "bg-emerald-700 text-white",
  dismissed: "bg-zinc-400 text-white",
};

function ChatLeadsTable({ leads, onStatusChange }) {
  if (!leads || leads.length === 0) {
    return (
      <div className="bg-white border border-[#DDD8C9] p-10 text-center">
        <div className="font-mono-label text-[10px] text-[#5C635F] tracking-widest">
          No chat leads yet
        </div>
        <div className="mt-2 text-[#151816] font-display font-bold text-lg">
          When Buddy captures a name + phone in the chat, it'll show up here.
        </div>
      </div>
    );
  }
  return (
    <div className="bg-white border border-[#DDD8C9]" data-testid="chat-leads-table">
      <Table>
        <TableHeader>
          <TableRow className="bg-[#F5F3EC]">
            <TableHead className="font-mono-label text-[10px] tracking-widest">
              Captured
            </TableHead>
            <TableHead className="font-mono-label text-[10px] tracking-widest">
              Name
            </TableHead>
            <TableHead className="font-mono-label text-[10px] tracking-widest">
              Phone
            </TableHead>
            <TableHead className="font-mono-label text-[10px] tracking-widest">
              What They Asked
            </TableHead>
            <TableHead className="font-mono-label text-[10px] tracking-widest text-right">
              Status
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {leads.map((l) => {
            const digits = (l.phone || "").replace(/\D/g, "");
            const pretty = digits.length === 10
              ? `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
              : l.phone_display || l.phone;
            const dt = l.created_at ? new Date(l.created_at) : null;
            return (
              <TableRow key={l.id} data-testid={`chat-lead-row-${l.id}`}>
                <TableCell className="text-xs text-[#5C635F] whitespace-nowrap">
                  {dt
                    ? dt.toLocaleString(undefined, {
                        month: "short",
                        day: "numeric",
                        hour: "numeric",
                        minute: "2-digit",
                      })
                    : "—"}
                </TableCell>
                <TableCell className="font-bold">{l.name}</TableCell>
                <TableCell>
                  <a
                    href={`tel:${digits}`}
                    className="text-[#151816] hover:text-[#5BB239] font-mono text-sm"
                  >
                    {pretty}
                  </a>
                </TableCell>
                <TableCell className="text-sm text-[#5C635F] max-w-md">
                  {l.summary || "—"}
                </TableCell>
                <TableCell className="text-right">
                  <Select
                    value={l.status || "new"}
                    onValueChange={(v) => onStatusChange(l.id, v)}
                  >
                    <SelectTrigger
                      className={`w-[130px] h-8 border-0 font-mono-label text-[10px] tracking-widest justify-end ${
                        leadStatusColors[l.status || "new"] || "bg-zinc-200"
                      }`}
                      data-testid={`chat-lead-status-${l.id}`}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="contacted">Contacted</SelectItem>
                      <SelectItem value="converted">Converted</SelectItem>
                      <SelectItem value="dismissed">Dismissed</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
